import { Form, FormikErrors, withFormik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useModal } from '@app/context/ModalsContext'

import { useLeverageOptions } from '@pages/Traders-Room/Trading-Accounts/hooks/useLeverageOptions'

import { useProductReadContext } from '@context/ProductContext'

import { Body2 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { PopoverInput } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { isTickmillEU, isTickmillUK } from '@utils/companyName.utils'
import { FormSubmitValues, IBPattern } from '@utils/formValidation'
import { isZero } from '@utils/validations'

import { CreateCampaignTradingAccount, CreateTradingAccount } from '@model/CreateTradingAccount'
import { isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'

import { usePasswordValidation } from '@global/PasswordValidation/PasswordValidation'
import { RawFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'
import { createFormPasswordField } from '@global/formField/FormPasswordField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { Loading } from '@shared/components/Loading/Loading'
import { CampaignAccountConfirmationModal } from '@shared/components/Navigation/modals/CampaignAccountConfirmation.modal'
import { ShortRiskWarningModal } from '@shared/modals/ShortRiskWarning.modal'

import { IBCodeField, NameField, ReferralField } from './CreateTradingAccountCampaign.components'
import {
  CampaignExtendedNameDto,
  CreateTradingAccountCampaignFormProps,
  CreateTradingAccountCampaignFormValues,
  OuterProps,
} from './CreateTradingAccountCampaign.types'
import {
  GenericReferralType,
  createInitialState,
  createReferralOptions,
  filterReferralOptions,
  shouldHideReferral,
} from './CreateTradingAccountCampaign.utils'
import { useModals } from './CreateTradingAccountCampaignModals'
import { useAccountTypesFetch } from './hooks/useAccountTypesFetch'
import { useNewTradingAccountData } from './hooks/useNewTradingAccountData'
import { usePlatformData } from './hooks/usePlatformData'
import { usePreselectEffect } from './hooks/usePreselectEffect'
import { useTradingAccountCreationOptionsFetch } from './hooks/useTradingAccountCreationOptionsFetch'
import { useValidateAccountIntroducingBroker } from './hooks/useValidateAccountIntroducingBroker'

import styles from '../TradingAccountsPage.module.scss'

export type PlatformDataType = ReturnType<typeof usePlatformData>

export type TradingAccountCreationOptionsFetchType = ReturnType<
  typeof useTradingAccountCreationOptionsFetch
>

const FormField = createFormField<CreateTradingAccountCampaignFormValues>()

const PasswordFormField = createFormPasswordField<CreateTradingAccountCampaignFormValues>()

const openedBonusCampaignModalLocationKeys = new Set<string>()

const CreateTradingAccountCampaignFormUI: React.FC<CreateTradingAccountCampaignFormProps> = (
  props
) => {
  const { isSubmitting, values, isValid, dirty, hasBonusCampaignQuery } = props
  const { isCurrenciesLoading, account } = props
  const { setFieldValue } = props
  const { handleSubmit } = props
  const location = useLocation()
  const { t } = useTranslation()

  const [isValidatingIBCode, setIsValidatingIBCode] = useState(false)
  const [isReferralSectionExpanded, setIsReferralSectionExpanded] = useState(false)
  const [allFieldsDisabled, setAllFieldsDisabled] = useState(false)
  const [currentIBValidation, setCurrentIBValidation] = useState(props.ibValidation)

  const tradingAccountCreationOptionsFetchProps = useTradingAccountCreationOptionsFetch(props)
  const { campaignType } = tradingAccountCreationOptionsFetchProps
  const { isLoading: isTACreationOptionsLoading } = tradingAccountCreationOptionsFetchProps
  const { accountTypes } = useAccountTypesFetch(props)

  const platformDataProps = usePlatformData(
    props,
    tradingAccountCreationOptionsFetchProps,
    allFieldsDisabled,
    accountTypes
  )
  const { isCampaign, isWalletDisabled, isLeverageDisabled, isAccountTypeDisabled } =
    platformDataProps

  const campaignAccountConfirmationModal = useModal(CampaignAccountConfirmationModal, {
    variant: 'informational',
  })
  const { platformsDisallowToOpenAccount } = platformDataProps

  useEffect(() => {
    if (hasBonusCampaignQuery && !openedBonusCampaignModalLocationKeys.has(location.key)) {
      openedBonusCampaignModalLocationKeys.add(location.key)
      campaignAccountConfirmationModal.open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBonusCampaignQuery, location.key])

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search)
    const tradingViewCampaignParam = urlSearchParams.get('tradingViewCampaign')
    const tradingviewPlatform = platformDataProps.platformsAllowToOpenAccount.find(
      (p) => p.platform.id === PlatformTypeEnum.TradingView
    )?.platform
    if (tradingViewCampaignParam === 'true' && tradingviewPlatform && !values.platformType?.id) {
      setFieldValue('platformType', tradingviewPlatform)
    }
  }, [location.search, platformDataProps.platformsAllowToOpenAccount, setFieldValue])

  const modalsProps = useModals(
    props,
    platformDataProps,
    tradingAccountCreationOptionsFetchProps,
    accountTypes
  )
  const { cancelModal, accountTypeModal, dynamicLeverageModal, markupModal, tradingPlatformModal } =
    modalsProps

  const { title, renderReferral } = useNewTradingAccountData(props, platformDataProps)
  const { ibCodeApiError } = useValidateAccountIntroducingBroker(
    props,
    setAllFieldsDisabled,
    setIsValidatingIBCode,
    setCurrentIBValidation
  )

  const { isPlatformFormDisabled } = usePreselectEffect(
    props,
    platformDataProps,
    tradingAccountCreationOptionsFetchProps
  )

  const { isPasswordValid, validationComponent } = usePasswordValidation({
    password: values.newTradingAccountPassword,
  })
  const isSelectedPlatformTMTorTV =
    isTMTPlatformType(values.platformType?.id) || isTradingViewPlatformType(values.platformType?.id)
  const shouldShowPasswordField = !isSelectedPlatformTMTorTV || !props.hasTMTOrTVAccount
  const isPasswordOk = shouldShowPasswordField ? isPasswordValid : true

  const hasReachedMaxAccounts = useMemo(
    () => platformsDisallowToOpenAccount.length > 0,
    [platformsDisallowToOpenAccount]
  )

  const toggleReferralSection = () => {
    setIsReferralSectionExpanded((prev) => !prev)
  }

  const isCurrencyFieldDisabled = allFieldsDisabled || !values.platformType || isCurrenciesLoading
  const campaignTermsUrl = useMemo(
    () =>
      values.campaignId
        ? props.data.find((c) => c.id === values.campaignId)?.termsAndConditions
        : undefined,
    [props.data, values.campaignId]
  )

  const isTradingPlatformFieldDisabled = useMemo(() => {
    return (
      (allFieldsDisabled && !isZero(values?.ibState?.id)) ||
      isPlatformFormDisabled ||
      isTACreationOptionsLoading
    )
  }, [allFieldsDisabled, values?.ibState?.id, isPlatformFormDisabled, isTACreationOptionsLoading])

  const riskWarningModal = useModal(ShortRiskWarningModal)
  const { isDefaultCFDProductType } = useProductReadContext()
  const isProductTypeCFD = isDefaultCFDProductType()

  const { leverages } = useLeverageOptions(values.platformType?.id, values.leverageType?.id)

  const handleAccountTypeSelect = (accountType: NameDto) => {
    if (accountType.name === 'VIP') {
      accountTypeModal.open({
        initialValue: accountType.id.toString(),
        initialShowVipContent: true,
      })
      return
    }
    modalsProps.handleSetAccountType(accountType)
  }

  const handleCampaignSelect = (campaign: CampaignExtendedNameDto) => {
    modalsProps.handleSetAccountOrCampaign(campaign)
    if (campaign.isCampaign) campaignAccountConfirmationModal.open()
  }

  return (
    <Loading showLoadingIcon isLoading={isTACreationOptionsLoading}>
      <FormTemplate
        title={title}
        goBack={cancelModal.open}
        renderIcon={
          isProductTypeCFD ? (
            <IconButton.Ghost
              icon={{ icon: WarningIcon, color: 'text-warning-high-em' }}
              onClick={() => riskWarningModal.open()}
            />
          ) : undefined
        }
      >
        <Form>
          <FieldWrapper>
            {!isTickmillEU(props.account) && !isTickmillUK(props.account) && (
              <NameField
                data={props.data}
                campaignId={values.campaignId}
                calculationType={values.calculationType}
                campaignType={campaignType}
                allFieldsDisabled={allFieldsDisabled}
                isCampaign={props.hasCampaignQuery}
                onSelect={handleCampaignSelect}
              />
            )}
            <FormField
              name='platformType.name'
              label={t('Trading Account.Trading Platform')}
              rightIcon={CaretIcon.Down}
              disabled={isTradingPlatformFieldDisabled}
              loading={isTACreationOptionsLoading}
              readOnly
              required
              onClick={tradingPlatformModal.open}
              key={values.platformType?.name ? 'platformType' : 'no-platformType'}
            />
            {hasReachedMaxAccounts && (
              <div className={styles.platformInfo}>
                <AlertIcon color='text-low-em' size={18} />
                <p className='ml-1'>
                  <span>
                    {t(
                      'Trading Account.You have reached the maximum amount of Live Trading Accounts for platform'
                    )}
                  </span>
                  <span className={styles.platformInfoStrong}>
                    {' '}
                    {platformsDisallowToOpenAccount.map((x) => x.platform.name).join(', ')}
                  </span>
                </p>
              </div>
            )}
            <PopoverInput
              name='currencyId'
              label={t('Campaigns.Currency')}
              placeholder={t('Campaigns.Currency')}
              value={values.currencyId}
              disabled={
                props.currencies.length < 2 ||
                isCurrenciesLoading ||
                (isCampaign ? isCurrencyFieldDisabled : isWalletDisabled)
              }
              required
              options={modalsProps.currencyOptions.map((currency) => ({
                id: currency,
                name: currency,
              }))}
              setValueOnSelect={false}
              onChange={(option) => modalsProps.handleCurrency(option.id)}
            />
            <PopoverInput
              name='accountType.name'
              label={t('Trading Account.Account Type')}
              value={values.accountType?.id}
              options={modalsProps.updatedAccountTypeName}
              setValueOnSelect={false}
              required
              disabled={allFieldsDisabled || isAccountTypeDisabled || isCurrenciesLoading}
              onChange={handleAccountTypeSelect}
              key={values.accountType?.id ? 'accountType' : 'no-accountType'}
            />
            <PopoverInput
              name='leverageType.name'
              label={t('Trading Account.Leverage')}
              value={values.leverageType?.id}
              options={leverages}
              setValueOnSelect={false}
              disabled={allFieldsDisabled || isLeverageDisabled}
              required
              onChange={modalsProps.handleSetLeverage}
              hint={
                isLeverageTypeDynamic(values.leverageType?.id) ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      dynamicLeverageModal.open()
                    }}
                    className='is-link'
                  >
                    {t('Trading Account.Leverage Information')}
                  </span>
                ) : undefined
              }
              key={values.leverageType?.id ? 'leverageType' : 'no-leverageType'}
            />
            {values.introducingBroker?.isCustomSpreadMarkup && (
              <RawFormCheckBoxField name='markup' disabled={!!allFieldsDisabled}>
                {t('Trading Account.Markup Modal')}
              </RawFormCheckBoxField>
            )}
            {values.campaignId && (
              <RawFormCheckBoxField
                name='terms'
                disabled={!!allFieldsDisabled}
                onChange={() => {
                  setFieldValue('terms', !values.terms)
                }}
              >
                {t('I have read all instructions and agree with')}{' '}
                {campaignTermsUrl ? (
                  <Link size='lg' href={campaignTermsUrl}>
                    {t('Terms & Conditions')}
                  </Link>
                ) : (
                  <span>{t('Terms & Conditions')}</span>
                )}{' '}
                {t('of the campaign')}
              </RawFormCheckBoxField>
            )}
            {renderReferral && !shouldHideReferral(props) && (
              <>
                <div className={styles.referralTitleWrapper}>
                  <Button.Ghost
                    iconLeft={isReferralSectionExpanded ? CaretIcon.Up : CaretIcon.Down}
                    onClick={toggleReferralSection}
                  >
                    {t('Trading Account.Referral Code')}
                  </Button.Ghost>
                  <div className={styles.infoButton}>
                    <Tooltip
                      heading={t('Trading Account.Referral Code')}
                      content={
                        <Body2>
                          {t(
                            'Trading Account.Referrals are agents who introduce new clients to Tickmill'
                          )}{' '}
                          {t(
                            'Trading Account.Enter the code that was provided to you by your Referral. The code consists of a combination of letters and digits.'
                          )}
                        </Body2>
                      }
                    >
                      <InfoIcon />
                    </Tooltip>
                  </div>
                </div>
                {isReferralSectionExpanded && (
                  <div className={styles.radioGroup}>
                    {createReferralOptions({ ...props, ibValidation: currentIBValidation })
                      .filter(filterReferralOptions(props))
                      .map((referral) => (
                        <ReferralField
                          key={referral.id}
                          referral={referral}
                          account={account}
                          isValidatingIBCode={isValidatingIBCode || !!referral.disabled}
                          setAllFieldsDisabled={setAllFieldsDisabled}
                        />
                      ))}
                  </div>
                )}{' '}
              </>
            )}
            <IBCodeField apiError={ibCodeApiError} onChange={() => setAllFieldsDisabled(true)} />
            {shouldShowPasswordField && (
              <>
                <PasswordFormField
                  name='newTradingAccountPassword'
                  label={t('Trading Account.New Trading Account Password')}
                  showLabel
                  type='password'
                  disabled={allFieldsDisabled}
                  hint={
                    isSelectedPlatformTMTorTV &&
                    t(
                      'Trading Account.Once set, the same password will be shared across all Tickmill Trader and TradingView accounts'
                    )
                  }
                  required
                />
                {validationComponent}
              </>
            )}
            <TransactionFormActionButtonTemplate>
              <Button.Secondary size='lg' onClick={() => cancelModal.open()}>
                {t('Cancel')}
              </Button.Secondary>
              <Button.Primary
                size='lg'
                loading={isSubmitting || isValidatingIBCode}
                disabled={
                  !(isPasswordOk && isValid && dirty && !isSubmitting && !allFieldsDisabled)
                }
                onClick={async () => {
                  if (values.introducingBroker?.isCustomSpreadMarkup) {
                    markupModal.open()
                  } else {
                    await handleSubmit()
                  }
                }}
              >
                {t('Confirm')}
              </Button.Primary>
            </TransactionFormActionButtonTemplate>
          </FieldWrapper>
        </Form>
      </FormTemplate>
    </Loading>
  )
}

function dataToSubmit(
  values: CreateTradingAccountCampaignFormValues
): FormSubmitValues<CreateTradingAccount | CreateCampaignTradingAccount> {
  const campaignId = values.campaignId && values.campaignId === '0' ? null : values.campaignId
  const isNoReferral = values.ibState?.id === GenericReferralType.None
  const data = {
    introducingBrokerId: isNoReferral ? null : values.introducingBroker?.id || null,
    receivesReports: true,
    leverageTypeId: values.leverageType?.id ?? null,
    platformTypeId: values.platformType?.id ?? null,
    tradingAccountTypeId: values.accountType?.id ?? null,
    password: values.newTradingAccountPassword || null,
  }
  return campaignId
    ? { ...data, currencyId: values.currencyId!, campaignId }
    : { ...data, currencyId: values.currencyId! }
}

export const CreateTradingAccountCampaignForm = withFormik<
  OuterProps,
  CreateTradingAccountCampaignFormValues
>({
  mapPropsToValues: (props) => createInitialState(props),
  handleSubmit: async (values, { props, setSubmitting }) => {
    setSubmitting(true)
    try {
      if (
        !shouldHideReferral(props) &&
        values.referralOptionId === GenericReferralType.New &&
        !values.ibCode
      ) {
        return
      }
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { hasCampaignQuery, t } = props
    const { campaignId } = values
    const validCampaignId = campaignId && campaignId !== '0'
    const errors: FormikErrors<CreateTradingAccountCampaignFormValues> = {}

    if (!values.currencyId) errors.currencyId = t('Validation.Required')

    if (!values.accountType) errors.accountType = t('Validation.Required')
    if (!values.platformType) errors.platformType = t('Validation.Required')
    if (!values.leverageType) errors.leverageType = t('Validation.Required')
    if (!values.terms && (hasCampaignQuery || validCampaignId)) {
      errors.terms = t('Validation.Required')
    }
    if (values.introducingBroker?.isCustomSpreadMarkup && !values.markup) {
      errors.markup = t('Validation.Required')
    }
    if (
      !shouldHideReferral(props) &&
      values.referralOptionId === GenericReferralType.New &&
      !values.ibCode
    ) {
      errors.ibCode = t('Validation.Required')
    }
    if (!shouldHideReferral(props) && values.ibCode && !IBPattern.test(values.ibCode)) {
      errors.ibCode = t('Validation.Referral code should begin with')
    }
    return errors
  },
  enableReinitialize: false,
  isInitialValid: false,
})(CreateTradingAccountCampaignFormUI)

export type {
  CampaignExtendedNameDto,
  CreateTradingAccountCampaignFormValues,
} from './CreateTradingAccountCampaign.types'
